import { AnalyticsContext } from '@carvana/eventable-clickstream';
import {
  SearchBar as MerchSearchBar,
  SearchBarProps
} from '@carvana/merch-search-bar';
import dynamic from 'next/dynamic';
import { useCallback, useContext, useMemo, useState } from 'react';
import { apikUrl, assetsCdnUrl } from '~/constants/environment';
import { useIsMobile } from '~/context/common/DeviceContext';
import { useConsumer } from '~/context/common/StaticContext';
import UserContext from '~/context/common/UserContext';
import ModalContext from '~/context/sem/ModalContext';
import { generateErrorLogger } from '~/utilities/loggers';

const SearchBarModal = dynamic(
  () => import(/* webpackChunkName: "SearchBarModal" */ './SearchBarModal'),
  { ssr: false }
);

const onSuggestionsApiError = generateErrorLogger('Get Suggestions', {});

type OwnProps = {
  id?: string;
};

export default function SearchBar({ id }: OwnProps) {
  const { trackEvent } = useContext(AnalyticsContext);
  const consumer = useConsumer();
  const { setSearchBarModalOpen, setIsSearchBarFocused } =
    useContext(ModalContext);
  const [inputValue, setInputValue] = useState('');
  const {
    location: {
      data: { state = '' }
    }
  } = useContext(UserContext);
  const isMobile = useIsMobile();

  const onCloseModal = useCallback(() => {
    setSearchBarModalOpen(false);
  }, [setSearchBarModalOpen]);

  const onShowModalClick = useCallback(() => {
    setSearchBarModalOpen(true);
  }, [setSearchBarModalOpen]);

  const searchBarProps: SearchBarProps = useMemo(
    () => ({
      apiBaseUrl: apikUrl,
      buttonSize: 'small',
      cdnBaseUrl: assetsCdnUrl,
      geolocationState: state,
      id,
      inputBgColor: 'gray',
      inputValue,
      onCloseModal,
      onFocus: setIsSearchBarFocused,
      onShowModalClick,
      onSuggestApiError: onSuggestionsApiError,
      refSource: consumer,
      rounded: 'md',
      setInputValue,
      showModal: isMobile,
      trackEvent: trackEvent
    }),
    [
      consumer,
      id,
      inputValue,
      isMobile,
      onCloseModal,
      onShowModalClick,
      setIsSearchBarFocused,
      state,
      trackEvent
    ]
  );

  return (
    <div className="z-2 h-48">
      <MerchSearchBar {...searchBarProps} />
      {isMobile && <SearchBarModal {...searchBarProps} />}
    </div>
  );
}
